import { useAppDispatch } from '@app/hooks/reduxHooks';
import { contentDirection } from '@app/utils/utils';
import { ConfigProvider } from 'antd';
import i18next from 'i18next';
import React, { useCallback, useEffect, useState } from 'react';
import { HelmetProvider } from 'react-helmet-async';
import 'typeface-lato';
import 'typeface-montserrat';
import { AppRouter } from './components/router/AppRouter';
import { useAppSelector } from './hooks/reduxHooks';
import { useAutoNightMode } from './hooks/useAutoNightMode';
import { useLanguage } from './hooks/useLanguage';
import { usePWA } from './hooks/usePWA';
import { useThemeWatcher } from './hooks/useThemeWatcher';
import { readToken } from './services/localStorage.service';
import { getTranslationList, getTranslationWithQuery } from './store/slices/translationsSlice';
import { fetchUser } from './store/slices/userSlice';
import GlobalStyle from './styles/GlobalStyle';
import { themeObject } from './styles/themes/themeVariables';
import { IUser } from './interfaces/user';
import FullScreenLoader from './components/common/FullScreenLoader/FullScreenLoader';

const App: React.FC = () => {
  const dispatch = useAppDispatch();
  const { language, setLanguage } = useLanguage();
  const [locale, setLocale] = useState();
  const [loader, setLoader] = useState(false);
  const theme = useAppSelector((state) => state.theme.theme) as keyof typeof themeObject;
  const translation = useAppSelector((state) => state.translation);
  const translationList = useAppSelector((state) => state.translation.translationList);
  const user = useAppSelector((state) => state.user.user);
  const token = readToken();

  usePWA();

  useAutoNightMode();

  useThemeWatcher();

  const shouldFetchUser = (token: string | null, user: IUser | null) => {
    return token && !user;
  };

  useEffect(() => {
    if (shouldFetchUser(token, user)) {
      dispatch(fetchUser());
    }
  }, [dispatch, token, user]);

  useEffect(() => {
    dispatch(getTranslationList());
  }, [dispatch]);

  useEffect(() => {
    if (language) {
      dispatch(getTranslationWithQuery({ isoCode: language }));
      setLanguage(language);
    }
  }, [dispatch, language, setLanguage]);

  const getTranslation = useCallback(async () => {
    if (translation && translation.isoCode) {
      i18next.addResourceBundle(translation.isoCode, 'translation', translation.translations, false, false);
    }
    const data = await import(`antd/es/locale/${translation.localHelper}`);
    setLocale(data);
  }, [translation]);

  useEffect(() => {
    getTranslation();
  }, [getTranslation, translation]);

  useEffect(() => {
    if (translationList && translationList.length > 0) {
      setLoader(true);
    }
  }, [translationList]);

  return (
    <>
      <meta name="theme-color" content={themeObject[theme].primary} />
      <GlobalStyle />
      <HelmetProvider>
        {!loader && <FullScreenLoader />}
        {loader && (
          <ConfigProvider locale={locale} direction={contentDirection(translation.isRtl)}>
            <AppRouter />
          </ConfigProvider>
        )}
      </HelmetProvider>
    </>
  );
};

export default App;
